import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loader from 'cccisd-loader';
import Tooltip from 'cccisd-tooltip';
import { useDispatch, useSelector } from 'react-redux';
import AssessmentCard from './Card';
import { loadAssessmentsAndContacts } from 'js/reducers/share';
import _intersection from 'lodash/intersection';
import style from './style.css';
// icons
import IconEnlarge from 'cccisd-icons/enlarge7';
import IconShrink from 'cccisd-icons/shrink7';
import IconSort from 'cccisd-icons/sort';
import IconPlus from 'cccisd-icons/plus-circle2';

const sortByDate = (data, ascending = false) => {
    return [...data].sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return ascending ? dateA - dateB : dateB - dateA;
    });
};

const AssessmentCards = props => {
    const {
        selectedStudents,
        forceReloadQueries,
        setForceReloadQueries,
        handleAssignAssessments,
        selectedStudentsList,
        filters,
    } = props;
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState([]);
    const [planList, setPlanList] = useState([]);
    const [isDescendingSort, setisDescendingSort] = useState(true);
    const dispatch = useDispatch();
    const assessmentPlanList = useSelector(state => state.app.share.assessmentPlans);

    useEffect(() => {
        const fetchStudentPlans = async () => {
            setLoading(true);
            try {
                dispatch(loadAssessmentsAndContacts());
            } catch (e) {
                throw e;
            } finally {
                setLoading(false);
            }
        };
        fetchStudentPlans();
    }, [forceReloadQueries]);

    useEffect(() => {
        if (assessmentPlanList.length > 0) {
            const initialCollapsed = assessmentPlanList
                .filter(plan => {
                    const planSettings = JSON.parse(plan.settings);
                    return !!planSettings.stopAssessing || !!plan.completedDate;
                })
                .map(plan => plan.assignmentPlanId);
            setCollapsed(initialCollapsed);
        }
    }, [assessmentPlanList]);

    useEffect(() => {
        setPlanList(filterAssessmentPlans());
    }, [assessmentPlanList, filters, selectedStudents]);

    const filterAssessmentPlans = () => {
        let plans = [...assessmentPlanList];

        if (filters?.archived === 'show') {
            plans = plans.filter(plan => plan.archivedDate);
        } else if (filters?.archived === 'hide') {
            plans = plans.filter(plan => !plan.archivedDate);
        }

        return plans.filter(plan => {
            const planSettings = JSON.parse(plan.settings);
            const planStopped = planSettings.stopAssessing && planSettings.stopAssessing;
            const matchesCompleted = filters?.completed && !!plan.completedDate;
            const matchesReady = filters?.ready && !plan.completedDate && !planStopped;
            const matchesStopped = filters?.stopped && planStopped;

            // If all progress checkbox filters are unselected (default state (false)), dont enforce any progress filter
            // If any progress filter is selected, enforce the filters
            const matchProgress =
                filters?.completed || filters?.ready || filters?.stopped
                    ? matchesCompleted || matchesReady || matchesStopped
                    : true;

            const matchesAssessment =
                [...filters.assessments].length === 0
                    ? true
                    : _intersection(plan.sessionList.map(s => s.settings.resourceIds).flat(), [...filters.assessments])
                          .length > 0;

            const matchesSelectedStudents =
                selectedStudents.length === 0
                    ? true
                    : selectedStudents.some(student => student.pawnId === plan.selectedByPawn.pawn.pawnId);

            return matchesAssessment && matchesSelectedStudents && matchProgress;
        });
    };

    if (loading) {
        return <Loader loading />;
    }

    const renderCards = () => {
        const buttonContent = (
            <div className="d-flex justify-content-between mb-3">
                <Tooltip
                    title={
                        selectedStudents.length === 1
                            ? `Select assessments for ${selectedStudentsList[0].name}`
                            : 'Select a student and select assessments for them'
                    }
                >
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handleAssignAssessments}
                        data-assign-assessments
                    >
                        <IconPlus /> Assign Assessments{' '}
                        {selectedStudentsList.length === 1 ? `for ${selectedStudentsList[0].name}` : ''}
                    </button>
                </Tooltip>
                {(planList.length > 0 || assessmentPlanList.length > 0) && (
                    <div>
                        <Tooltip title="Expand all">
                            <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={() => {
                                    setCollapsed([]);
                                }}
                            >
                                <IconEnlarge />
                            </button>
                        </Tooltip>
                        <Tooltip title="Collapse all">
                            <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={() => {
                                    setCollapsed(planList.map(plan => plan.assignmentPlanId));
                                }}
                            >
                                <IconShrink />
                            </button>
                        </Tooltip>
                        <Tooltip title={`Sort by date ${isDescendingSort ? 'ascending' : 'descending'}`}>
                            <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={() => {
                                    setPlanList(prevList => {
                                        const sortedList = sortByDate(prevList, isDescendingSort);
                                        setisDescendingSort(!isDescendingSort);
                                        return sortedList;
                                    });
                                }}
                            >
                                <IconSort />
                            </button>
                        </Tooltip>
                    </div>
                )}
            </div>
        );

        if (assessmentPlanList.length === 0) {
            return (
                <>
                    <div>You have no assigned assessments. Click on Assign Assessments to get started.</div>
                    {buttonContent}
                </>
            );
        }

        if (planList.length === 0) {
            if (filters?.archived === 'show') {
                return (
                    <>
                        {buttonContent}
                        <div>No archived assessments.</div>
                    </>
                );
            }
            return (
                <>
                    {buttonContent}
                    <div>No assessments match the selected filters.</div>
                </>
            );
        }

        return (
            <>
                {buttonContent}
                {filters?.archived === 'show' && <h4>Archived ({planList.length})</h4>}
                {planList.map(plan => (
                    <AssessmentCard
                        key={plan.assignmentPlanId}
                        plan={plan}
                        collapsed={collapsed}
                        setCollapsed={setCollapsed}
                        setForceReloadQueries={setForceReloadQueries}
                        filters={filters}
                    />
                ))}
            </>
        );
    };

    return <div className={style.assessmentCards}>{renderCards()}</div>;
};

export default AssessmentCards;

AssessmentCards.propTypes = {
    selectedStudents: PropTypes.array,
    forceReloadQueries: PropTypes.number,
    setForceReloadQueries: PropTypes.func,
    filters: PropTypes.shape({
        ready: PropTypes.bool,
        stopped: PropTypes.bool,
        completed: PropTypes.bool,
        assessments: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        archived: PropTypes.string,
    }),
    handleAssignAssessments: PropTypes.func,
    selectedStudentsList: PropTypes.array,
};
AssessmentCards.defaultProps = {};
